// @ts-nocheck
import React, { useState } from 'react';
import {
  Bell,
  BellOff,
  Check,
  X,
  AlertTriangle,
  Loader2,
  Send,
  Smartphone,
} from 'lucide-react';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { InstallIosBanner } from '../ui/InstallIosBanner';

const STATUS_CONFIG: Record<string, { label: string; Icon: any }> = {
  loading:          { label: 'Verificando...', Icon: Loader2 },
  unsupported:      { label: 'Não suportado',  Icon: X },
  denied:           { label: 'Bloqueado',      Icon: X },
  'not-subscribed': { label: 'Desativado',     Icon: BellOff },
  subscribing:      { label: 'Ativando...',    Icon: Loader2 },
  subscribed:       { label: 'Ativo',          Icon: Check },
  error:            { label: 'Erro',           Icon: AlertTriangle },
};

const STATUS_CLASSES: Record<string, string> = {
  loading:          'bg-muted text-muted-foreground',
  unsupported:      'bg-destructive/10 text-destructive',
  denied:           'bg-destructive/10 text-destructive',
  'not-subscribed': 'bg-yellow-100 text-yellow-800',
  subscribing:      'bg-primary/10 text-primary',
  subscribed:       'bg-green-100 text-green-800',
  error:            'bg-destructive/10 text-destructive',
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // @ts-ignore
  if (window.navigator.standalone) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
};

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua);
};

const PwaNotifications: React.FC = () => {
  const { status, error, subscribe, unsubscribe } = usePushNotifications();
  const [testing, setTesting] = useState(false);
  const [testSent, setTestSent] = useState<'ok' | 'err' | null>(null);

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.loading;
  const Icon = cfg.Icon;
  const isLoading = status === 'loading' || status === 'subscribing';

  const sendLocalTest = async () => {
    setTesting(true);
    setTestSent(null);
    try {
      if (Notification.permission !== 'granted') throw new Error('Permissão não concedida');
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification('🧪 Teste de notificação', {
        body: 'Se você está vendo isso, está tudo certo!',
        icon: '/logoasad.png',
        badge: '/logoasad.png',
        tag: 'pwa-test',
        vibrate: [200, 100, 200],
        data: { url: '/pwa/notifications' },
      });
      setTestSent('ok');
    } catch (e) {
      console.error(e);
      setTestSent('err');
    } finally {
      setTesting(false);
      setTimeout(() => setTestSent(null), 4000);
    }
  };

  const iosNotInstalled = isIos() && !isStandalone();

  return (
    <div>
      <InstallIosBanner />

      <div className="px-4 pt-4 pb-2">
        <h1 className="text-lg font-bold">Notificações</h1>
        <p className="text-sm text-muted-foreground">
          Receba alertas de agenda, prazos, atendimentos e financeiro no celular e desktop.
        </p>
      </div>

      <div className="space-y-4 p-4">
        {/* Aviso iOS não instalado */}
        {iosNotInstalled && (
          <Card className="border-amber-300 bg-amber-50/60">
            <CardContent className="p-4 flex gap-3">
              <div className="p-2 rounded-xl bg-amber-200/50 shrink-0">
                <Smartphone className="h-5 w-5 text-amber-800" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-amber-900">Instale antes de ativar</p>
                <p className="text-amber-800 mt-1 leading-snug">
                  O iPhone só entrega push quando o app está instalado na tela inicial. No Safari,
                  toque em <strong>Compartilhar → Adicionar à Tela de Início</strong>. Depois abra
                  pelo ícone novo e volte aqui para ativar.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="h-5 w-5 text-primary" />
              Notificações push
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge
                className={`${
                  STATUS_CLASSES[status] || STATUS_CLASSES.loading
                } flex items-center gap-1 text-xs font-medium border-0`}
              >
                <Icon className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
                {cfg.label}
              </Badge>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                <p className="text-xs text-destructive">{error}</p>
              </div>
            )}

            {status === 'denied' && (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 space-y-2 text-xs">
                <p className="font-medium text-yellow-900">Como liberar:</p>
                <ul className="text-yellow-800 space-y-1 list-disc list-inside">
                  <li>
                    <strong>iPhone:</strong> Ajustes → Notificações → procure este app → Permitir Notificações.
                  </li>
                  <li>
                    <strong>Android/Chrome:</strong> toque no cadeado na barra de URL → Configurações do site → Notificações → Permitir.
                  </li>
                </ul>
              </div>
            )}

            {(status === 'not-subscribed' || status === 'error') && (
              <Button
                onClick={subscribe}
                className="w-full h-11 text-sm font-semibold"
                disabled={isLoading}
                size="lg"
              >
                <Bell className="h-4 w-4 mr-2" />
                Ativar notificações
              </Button>
            )}

            {status === 'subscribed' && (
              <div className="space-y-2">
                <Button
                  onClick={sendLocalTest}
                  variant="outline"
                  className="w-full h-11"
                  disabled={testing}
                >
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  {testSent === 'ok'
                    ? 'Notificação enviada!'
                    : testSent === 'err'
                    ? 'Falhou — tente novamente'
                    : 'Enviar notificação de teste'}
                </Button>
                <Button
                  onClick={unsubscribe}
                  variant="ghost"
                  className="w-full text-muted-foreground text-xs"
                >
                  <BellOff className="h-3 w-3 mr-1" />
                  Desativar notificações neste dispositivo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* O que você vai receber */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">O que você vai receber</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {[
              { emoji: '📋', text: 'Tarefas pessoais antes do vencimento' },
              { emoji: '⚖️', text: 'Prazos processuais (1 dia antes + na hora)' },
              { emoji: '👤', text: 'Atendimentos agendados com cliente' },
              { emoji: '💚', text: 'Valores a receber — hoje e amanhã às 10h' },
              { emoji: '💸', text: 'Contas a pagar — hoje e amanhã às 10h' },
              { emoji: '🔒', text: 'Sempre segmentadas: só você recebe as suas' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-2">
                <span className="text-base">{item.emoji}</span>
                <span className="text-muted-foreground">{item.text}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PwaNotifications;
